import React, { useContext, useState } from 'react';
import { arrayOf, string, shape, number, object } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import { urlTransformer } from 'nav-utilities';
import Link from '../Link';
import {
  CopyrightListItem,
  footerList,
  SubFooterDropDownContainer,
  StyledSubFooter,
  SubFooterLink,
  footerDropDown,
  linkContainer,
} from './styles';
import useMenuData from './../../utils/hooks/cmsDataHook';
import { MENU_TYPES, DATA_TYPES, BODEGA_DEFAULTS, BOLD_PRIVACY_FOOTER_LINK } from '../../constants';
import DropDownMenu from '../DropDownMenu';
import FormattedTranslation from '../FormattedTranslation';
import { AnalyticsContext } from '../../context/analyticsContext';
import AnalyticsEventTypes from './../../utils/analyticsEvent';
import LinkColumn from '../LinkColumn';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';

/**
 * An individual link in the sub-footer.
 * @param link {NavMenuItem}
 * @param props {Object}
 * @returns {*}
 */
function SubFooterItem({ link, ...props }) {
  const shouldBoldPrivacyLink = useFeatureFlags(BOLD_PRIVACY_FOOTER_LINK);
  const { analyticsClient } = useContext(AnalyticsContext);

  return (
    <SubFooterLink
      {...props}
      aria-label={link.label}
      href={link.url}
      onClick={() => {
        analyticsClient?.clickNavFooter({ path: link.classification, hashKey: link.hashKey });
      }}
      path={link.classification}
      target={link.target}
    >
      {link.image && (
        <img
          alt=""
          aria-hidden="true"
          src={urlTransformer(link.image)}
          style={{ objectFit: 'contain' }}
        />
      )}
      {/* This bolds the privacy link in Korea. */}
      <TextStyled
        appearance="body2Strong"
        bold={shouldBoldPrivacyLink && link.classification?.includes('privacy')}
        style={{ paddingLeft: `${link.image ? '2px' : '0px'}` }}
      >
        {link.label}
      </TextStyled>
    </SubFooterLink>
  );
}

SubFooterItem.propTypes = {
  link: shape({
    url: string,
    classification: string,
    target: string,
    image: string,
    label: string,
  }),
};

/**
 * A dropdown menu in the sub-footer, has a SubFooterItem as a heading.
 * This is used for the Guides menu, for example.
 * @param link {NavMenuItem}
 * @returns {*}
 */
function SubFooterItemDropdown({ link }) {
  const { url, path, hashKey, children, label } = link;
  const dropDownItems = {
    ...link,
    content: children,
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_FOOTER,
  };
  const [isMenuOpen, setMenuOpen] = useState(false);
  const analyticsPayload = {
    props: {
      path: path,
      hashKey: hashKey,
    },
    type: AnalyticsEventTypes.CLICK_NAV_FOOTER,
  };

  /**
   * A Wrapper for links in the sub-footer.
   * Only available when link is within drop down menu.
   * @param dropDownItems {NavMenuItem}
   * @returns {*}
   */
  const LinkWrapper = dropDownItems ? React.Fragment : Link;

  /**
   * Drop down menu props.
   * Only set when link is within drop down menu.
   */
  const linkProps = {};
  if (url) {
    linkProps.clickEventPayload = analyticsPayload;
    linkProps.href = url;
  }

  /**
   * Props for drop down menu.
   * Builds LinkWrapper with linkProps.
   * When url available, user is able to navigate specifically to link.
   * Otherwise user selects text and menu caret on one click.
   * @param url {NavMenuItem}
   * @param linkProps {NavMenuItem}
   * @param isMenuOpen {Boolean}
   * @param label {String}
   */
  const dropDownMenuProps = {
    [url ? 'menuLink' : 'styledSummaryText']: (
      <LinkWrapper {...linkProps}>
        <TextStyled
          appearance="body2Strong"
          color={isMenuOpen ? 'primary' : 'secondary'}
          data-testid={`desktop-user-menu-item-message-${label}`}
        >
          {label}
        </TextStyled>
      </LinkWrapper>
    ),
  };

  return (
    <DropDownMenu
      MenuContainerComponent={SubFooterDropDownContainer}
      detailsContentCustomClass={footerDropDown}
      isMenuOpen={isMenuOpen}
      menuLabel={label}
      setMenuOpen={setMenuOpen}
      testId={`${label}-sub-footer-dropdown`}
      {...dropDownMenuProps}
    >
      {dropDownItems && (
        <LinkColumn
          containerClassName={linkContainer}
          content={dropDownItems}
          textAppearance={'body2Strong'}
        />
      )}
    </DropDownMenu>
  );
}

SubFooterItemDropdown.propTypes = {
  link: shape({
    url: string,
    analyticsEventName: string,
    path: arrayOf(string),
    label: string,
    children: arrayOf(object),
  }),
  itemIndex: number,
};

const SubFooter = () => {
  const menu = useMenuData(DATA_TYPES.FOOTER, MENU_TYPES.BOTTOM_FOOTER);
  const { COPY_RIGHTS } = BODEGA_DEFAULTS;
  if (!menu) return null;
  const currentYear = new Date().getFullYear();
  return (
    <StyledSubFooter>
      <ul aria-label="sub footer" className={footerList}>
        <CopyrightListItem>
          <TextStyled appearance="body2Strong" color={'secondary'}>
            <FormattedTranslation
              defaultValue={`© ${currentYear} ${COPY_RIGHTS}`}
              translationId={'nav_copyright'}
              values={{
                current_year: currentYear,
              }}
            />
          </TextStyled>
        </CopyrightListItem>
        {menu?.children?.map((link, index) => {
          const key = `SubFooterItem-${index}`;
          return (
            <li key={key}>
              {link.children.length ? (
                <SubFooterItemDropdown index={index} link={link} />
              ) : (
                <SubFooterItem link={link} />
              )}
            </li>
          );
        })}
      </ul>
    </StyledSubFooter>
  );
};

export default SubFooter;
